/**
 * Keeps author-typed blank lines through a markdown round trip.
 *
 * An empty paragraph serializes to nothing, so two blocks separated by one blank line come out as a run of four
 * newlines — and markdown treats any run of blank lines as a single block break, so re-parsing drops it. Writing the
 * blank line as an `&#x20;` paragraph makes it a real block that survives (verified against mdast-util-to-markdown:
 * the entity is preserved verbatim and re-parses as a paragraph). It renders as a space, i.e. an empty-looking line.
 */

/** Paragraph body used to hold a blank line open. A character reference for a space. */
const BLANK_LINE_PARAGRAPH = '&#x20;'

/**
 * Rewrites runs of blank lines as explicit `&#x20;` paragraphs. Newlines inside fenced or inline code are left
 * alone — blank lines are content there, and rewriting them would corrupt the sample.
 */
export const preserveBlankLines = (markdown: string): string => {
   const codeBlocks: string[] = []
   const withoutCode = markdown.replace(/(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g, match => {
      codeBlocks.push(match)
      return `__GAPBLANKCODE${codeBlocks.length - 1}__`
   })

   // A run of N newlines separates two blocks by N/2 - 1 empty paragraphs (2 newlines is the normal separator).
   const withBlankParagraphs = withoutCode.replace(/\n{4,}/g, run => {
      const blankParagraphs = Math.floor(run.length / 2) - 1
      return '\n\n' + `${BLANK_LINE_PARAGRAPH}\n\n`.repeat(blankParagraphs)
   })

   return withBlankParagraphs.replace(/__GAPBLANKCODE(\d+)__/g, (_match, index) => codeBlocks[Number(index)])
}

/** Whether markdown holds no content beyond whitespace and blank-line or non-breaking-space placeholders. */
export const isBlankMarkdown = (markdown: string): boolean => {
   return markdown.replaceAll(BLANK_LINE_PARAGRAPH, '').replaceAll('&nbsp;', '').replaceAll('\u00a0', '').trim() === ''
}
